// public/js/modules/auth.js
const TOKEN_KEY = 'superb_tracking_auth';
const USER_KEY = 'superb_tracking_user';
const PKCE_VERIFIER_KEY = 'pkce_code_verifier';

function getConfig() { return window.APP_CONFIG || {}; }
function getCognitoConfig() { return getConfig().COGNITO || {}; }

// Turn an API path into the URL to actually call.
//
// On the web this resolves against <base href>, which is exactly what a bare
// fetch('api/v1/...') already did — so nothing changes. It exists for the case
// where the page is NOT served from the same origin as the API: a packaged
// mobile shell runs on capacitor://localhost or file://, where a relative path
// points at the bundle instead of the backend. Such a build sets API_BASE and
// every call follows, because they all come through authenticatedFetch.
//
// Absolute URLs pass through untouched (presigned S3 links, Cognito endpoints).
export function apiUrl(pathOrUrl) {
  const p = String(pathOrUrl || '');
  if (/^[a-z][a-z0-9+.-]*:/i.test(p) || p.startsWith('//')) return p;
  const base = getConfig().API_BASE || '';
  if (!base) return new URL(p, document.baseURI).href;
  // Drop a leading slash so an API_BASE that carries a path keeps it.
  return new URL(p.replace(/^\/+/, ''), base.endsWith('/') ? base : base + '/').href;
}

function parseJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
  } catch { return null; }
}

function storeAuth(tokens) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  const idPayload = tokens.id_token ? parseJwt(tokens.id_token) : null;
  const accessPayload = tokens.access_token ? parseJwt(tokens.access_token) : null;
  const payload = idPayload || accessPayload || {};

  const user = {
    userId: payload.sub,
    username: payload['cognito:username'] || payload.username || payload.email?.split('@')[0] || 'User',
    email: payload.email || null,
    groups: payload['cognito:groups'] || [],
    exp: payload.exp,
  };
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

function loadAuth() {
  try {
    const tokens = JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null');
    const user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    if (!tokens || !user) return null;
    return { tokens, user };
  } catch {
    clearAuth();
    return null;
  }
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  // Additionally remove all possible leftovers
  localStorage.removeItem('superb_tracking_auth');
  localStorage.removeItem('superb_tracking_user');
}

// ====================== Refresh Token ======================
export async function refreshTokens() {
  const stored = JSON.parse(localStorage.getItem(TOKEN_KEY) || '{}');
  const refreshToken = stored.refresh_token;
  if (!refreshToken) throw new Error('No refresh_token available');

  const cognito = getCognitoConfig();
  const cleanDomain = cognito.domain.replace(/^https?:\/\//, '').replace(/\/$/, '');

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: cognito.clientId,
    refresh_token: refreshToken
  });

  const res = await fetch(`https://${cleanDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString()
  });

  if (!res.ok) {
    console.error('[AUTH] Refresh failed');
    clearAuth();
    throw new Error('Refresh token invalid or expired');
  }

  const newTokens = await res.json();
  const updated = { ...stored, ...newTokens };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(updated));

  if (newTokens.id_token) {
    const p = parseJwt(newTokens.id_token);
    const user = {
      userId: p.sub,
      username: p['cognito:username'] || p.username || p.email?.split('@')[0],
      email: p.email,
      groups: p['cognito:groups'] || [],
      exp: p.exp,
    };
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  return updated;
}

// ====================== CORRECTED authenticatedFetch ======================
export async function authenticatedFetch(url, options = {}) {
  let auth = loadAuth();

  if (!auth?.tokens?.access_token) {
    clearAuth();
    if (window.initiateLogin) window.initiateLogin();
    throw new Error('Not authenticated');
  }

  // Proactive refresh (when the token expires in < 5 minutes)
  const now = Math.floor(Date.now() / 1000);
  if (auth.user?.exp && (auth.user.exp - now) < 300) {
    try {
      await refreshTokens();
      auth = loadAuth();
    } catch (e) {
      console.warn('[AUTH] Proactive refresh failed');
    }
  }

  let token = auth?.tokens?.access_token;

  // Assemble the header correctly
  const headers = {
    ...(options.headers || {}),
    'Authorization': `Bearer ${token}`
  };

  // IMPORTANT: use the plain fetch here, NOT window.authenticatedFetch!
  const target = apiUrl(url);
  let res = await fetch(target, { ...options, headers });

  // Single retry on 401
  if (res.status === 401) {
    try {
      await refreshTokens();
      const newAuth = loadAuth();
      if (newAuth?.tokens?.access_token) {
        headers.Authorization = `Bearer ${newAuth.tokens.access_token}`;
        res = await fetch(target, { ...options, headers });
      }
    } catch {
      clearAuth();
      if (window.initiateLogin) window.initiateLogin();
      throw new Error('Session expired. Please log in again.');
    }
  }

  return res;
}

// ====================== Original login functions (restored) ======================
export async function initiateLogin() {
  const cognito = getCognitoConfig();
  if (!cognito.domain || !cognito.clientId) {
    alert('Cognito configuration missing');
    return;
  }

  const cleanDomainStr = cognito.domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const basePath = getConfig().PUBLIC_BASE_PATH || '';
  const redirectUri = `${window.location.origin}${basePath}`.replace(/\/?$/, '/');

  const codeVerifier = btoa(String.fromCharCode(...new Uint8Array(32).map(() => Math.floor(Math.random() * 256))))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  localStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);

  const params = new URLSearchParams({
    client_id: cognito.clientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256'
  });

  window.location.href = `https://${cleanDomainStr}/oauth2/authorize?${params.toString()}`;
}

export async function handleAuthCallback() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  if (!code) return null;

  const cognito = getCognitoConfig();
  const cleanDomainStr = cognito.domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const basePath = getConfig().PUBLIC_BASE_PATH || '';
  const redirectUri = `${window.location.origin}${basePath}`.replace(/\/?$/, '/');

  const codeVerifier = localStorage.getItem(PKCE_VERIFIER_KEY);
  if (!codeVerifier) return null;

  try {
    const tokenUrl = `https://${cleanDomainStr}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cognito.clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier
    });

    const res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    });

    if (!res.ok) return null;

    const tokens = await res.json();
    const user = storeAuth(tokens);
    localStorage.removeItem(PKCE_VERIFIER_KEY);
  
    // === IMPORTANT: always clean up the URL ===
    history.replaceState(null, '', window.location.pathname);
  
    return user;
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function checkAuth() {
  await handleAuthCallback();
  let auth = loadAuth();
  if (!auth) return null;

  const now = Math.floor(Date.now() / 1000);
  if (auth.user?.exp && auth.user.exp < now + 60 && auth.tokens?.refresh_token) {
    try {
      await refreshTokens();
      auth = loadAuth();
    } catch {
      clearAuth();
      return null;
    }
  }
  return auth ? auth.user : null;
}

export function showLogin() {
  initiateLogin();
}

function humanizeRole(groups) {
  const g = Array.isArray(groups) ? groups[0] : null;
  if (!g) return 'User';
  // PlatformAdmin → "Platform Admin", TenantAdmin → "Tenant Admin", …
  return String(g).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
}

function userInitials(name) {
  const parts = String(name || '').replace(/@.*/, '').split(/[.\s_-]+/).filter(Boolean);
  const s = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  return (s || 'U').toUpperCase();
}

export function showUser(user) {
  const topInfo = document.getElementById('user-info-top');
  const displayName = user.email?.split('@')[0]?.replace(/[._-]+/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase())
    || user.username || 'User';
  const role = humanizeRole(user.groups);
  // Platform-admin dropdown items: superadmin OR admin (the former superadmin cohort).
  const isSuper = (user.groups || []).some(g => g === 'superadmin' || g === 'admin');
  // Device distribution is internal operations, not billing: platformSupport
  // belongs there (and the backend has always let it in), while #subscription
  // stays with superadmin/admin. Mirrors isPlatformLevel in modules/rbac.js -
  // kept inline because this runs before the bundle's rbac import chain.
  const isPlatform = isSuper || (user.groups || []).includes('platformSupport');

  if (topInfo) {
    topInfo.innerHTML = `
      <div class="dropdown topbar-user">
        <button class="topbar-user-btn" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          <span class="topbar-avatar">${userInitials(user.email || user.username)}</span>
          <span class="topbar-user-meta d-none d-lg-flex">
            <span class="topbar-user-name">${displayName}</span>
            <span class="topbar-user-role">${role}</span>
          </span>
          <i class="fa-solid fa-chevron-down topbar-user-caret d-none d-lg-inline"></i>
        </button>
        <ul class="dropdown-menu dropdown-menu-end shadow">
          <li><span class="dropdown-item-text small text-muted">${user.email || user.username || ''}</span></li>
          <li><hr class="dropdown-divider"></li>
          ${isSuper ? `<li><a class="dropdown-item" href="#subscription"><i class="fa-solid fa-tags me-2"></i>Subscription plan</a></li>` : ''}
          ${isPlatform ? `<li><a class="dropdown-item" href="#distribution"><i class="fa-solid fa-boxes-stacked me-2"></i>Device distribution</a></li>` : ''}
          ${(user.groups || []).includes('superadmin') ? `<li><a class="dropdown-item" href="#user-activity"><i class="fa-solid fa-chart-line me-2"></i>User activity</a></li>` : ''}
          <li><a class="dropdown-item d-flex align-items-center" href="#whats-new"><i class="fa-solid fa-bullhorn me-2"></i>What's new<span id="whats-new-badge" class="badge bg-danger ms-auto d-none">0</span></a></li>
          <li><a class="dropdown-item" href="#settings"><i class="fa-solid fa-gear me-2"></i>Settings</a></li>
          <!-- The plane used to be the big Logout button in the sidebar. That
               button was a duplicate of this entry, so it went; the plane lives
               on here. On hover it banks left and flies out past the menu edge
               (clipped there, so it really disappears), and returns when the
               pointer leaves. Suppressed on touch, where there is no hover and
               the flight would only fire on the tap that logs you out. -->
          <li><button class="dropdown-item text-danger logout-item" type="button" id="topbar-logout">
            <span class="logout-plane" aria-hidden="true"><i class="fa-solid fa-paper-plane"></i></span>Logout
          </button></li>
        </ul>
      </div>
    `;
    document.getElementById('topbar-logout')?.addEventListener('click', () => {
      if (confirm('Really log out?')) initiateLogout();
    });
  }

  const sidebarInfo = document.getElementById('user-info-sidebar');
  if (sidebarInfo) {
    sidebarInfo.innerHTML = `
      <span class="text-success">✓</span>
      <strong>${user.email || user.username || 'No email'}</strong>
    `;
  }
}

// ====================== PROPER COGNITO LOGOUT ======================
export function initiateLogout() {
  clearAuth();

  const cognito = getCognitoConfig();
  const appConfig = getConfig();

  if (!cognito.domain || !cognito.clientId) {
    console.warn('[AUTH] No Cognito configuration — local logout only');
    window.location.replace(window.location.pathname);
    return;
  }

  const cleanDomain = cognito.domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const basePath = appConfig.PUBLIC_BASE_PATH || '';
  const logoutUri = `${window.location.origin}${basePath}`.replace(/\/?$/, '/');

  const params = new URLSearchParams({
    client_id: cognito.clientId,
    logout_uri: logoutUri
  });

  // Important: full navigation (no replace) so Cognito actually ends the session
  window.location.href = `https://${cleanDomain}/logout?${params.toString()}`;
}

export function showLoggedOutScreen() {
  const root = document.getElementById('root');
  if (!root) return;

  root.innerHTML = `
    <div class="d-flex align-items-center justify-content-center vh-100 bg-dark">
      <div class="text-center text-white">
        <h2 class="mb-4">You have been logged out.</h2>
        <button id="btn-login-again" class="btn btn-primary btn-lg px-5">
          <i class="fa-solid fa-sign-in-alt me-2"></i> Log in now
        </button>
      </div>
    </div>
  `;

  document.getElementById('btn-login-again').onclick = () => {
    initiateLogin();
  };
}


// Expose globally
window.authenticatedFetch = authenticatedFetch;
window.initiateLogin = initiateLogin;